import { CountrySelector } from "@/components/auth/CountrySelector.tsx";
import { SmsConsentCheckbox } from "@/components/auth/SmsConsentCheckbox.tsx";
import { VerificationCodeInput } from "@/components/auth/VerificationCodeInput.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { confirmationToast } from "@/components/ui/toast.tsx";
import { ALREADY_YOUR_NUMBER, changePhoneFailureMessage } from "@/shared/changePhoneCopy.ts";
import { countryList } from "@/shared/countryList.ts";
import { applyPhoneEdit, defaultCountryName, toE164 } from "@/shared/phoneInput.ts";
import {
  useResendPhoneCode,
  useSubmitPhoneNumber,
  useVerifyPhoneCode,
} from "@/views/Onboarding/hooks/usePhoneVerification.tsx";
import { useForm } from "@tanstack/react-form";
import { isAxiosError } from "axios";
import { useEffect, useRef, useState } from "react";

/**
 * `change` moves the account from its number to a new one (Account settings);
 * `add` gives an account with no verified number one (the Text Messages
 * connector). The steps are the same; only the words differ.
 */
type PhoneDialogMode = "add" | "change";

const COPY: Record<
  PhoneDialogMode,
  { title: string; description: string; codeNote: string; verified: string }
> = {
  change: {
    title: "Change phone number",
    description:
      "We'll text a code to your new number. Your current number stays until the code is verified.",
    codeNote: " Once it's verified, other devices and browsers will need to sign in again.",
    verified: "Phone number updated",
  },
  add: {
    title: "Add your phone number",
    description: "We'll text a code to your number. Once it's verified, Okthx can text you.",
    codeNote: "",
    verified: "Phone number added",
  },
};

/**
 * Moves the account to a new phone number, or adds one to an account without
 * one. Nothing changes until the code sent to the number verifies; closing the
 * dialog before then leaves the account as it was.
 */
export const ChangePhoneNumberDialog = ({
  open,
  onOpenChange,
  currentNumber,
  mode = "change",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The account's number now; absent when adding one. */
  currentNumber?: string;
  mode?: PhoneDialogMode;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[425px]">
      {/* Inside the content, so closing unmounts it and the next open starts
          over at the number step. */}
      <ChangePhoneNumberSteps
        currentNumber={currentNumber}
        mode={mode}
        onDone={() => onOpenChange(false)}
      />
    </DialogContent>
  </Dialog>
);

/** The number step until a code goes out, then the code step for that number. */
const ChangePhoneNumberSteps = ({
  currentNumber,
  mode,
  onDone,
}: {
  currentNumber?: string;
  mode: PhoneDialogMode;
  onDone: () => void;
}) => {
  // The number as typed, once a code has been sent to it.
  const [sentTo, setSentTo] = useState<string | null>(null);

  return sentTo === null ? (
    <NumberStep currentNumber={currentNumber} mode={mode} onSent={setSentTo} onCancel={onDone} />
  ) : (
    <CodeStep sentTo={sentTo} mode={mode} onBack={() => setSentTo(null)} onVerified={onDone} />
  );
};

/** The new number and the consent that pays for the text. */
const NumberStep = ({
  currentNumber,
  mode,
  onSent,
  onCancel,
}: {
  currentNumber?: string;
  mode: PhoneDialogMode;
  onSent: (formatted: string) => void;
  onCancel: () => void;
}) => {
  // The country lives outside the form because it is not submitted: it decides
  // how the typed digits are grouped and what they mean.
  const [countryName, setCountryName] = useState(() => defaultCountryName(countryList));
  const country = countryList.find((c) => c.name === countryName) ?? countryList[0];
  const [error, setError] = useState("");
  const submitPhoneNumber = useSubmitPhoneNumber();

  const form = useForm({
    defaultValues: { phoneNumber: "", smsConsent: false },
    onSubmit: async ({ value }) => {
      const e164 = toE164(value.phoneNumber, country);
      if (e164 === null || !value.smsConsent) return;
      if (e164 === currentNumber) {
        setError(ALREADY_YOUR_NUMBER);
        return;
      }
      setError("");
      try {
        const resp = await submitPhoneNumber.mutateAsync({
          phoneNumber: e164,
          smsConsent: value.smsConsent,
        });
        // The stored number need not be E.164, so the server is the one that
        // recognizes it in every form.
        if (resp.alreadyVerified) {
          setError(ALREADY_YOUR_NUMBER);
          return;
        }
        onSent(value.phoneNumber);
      } catch (err) {
        setError(changePhoneFailureMessage(err, "We couldn't send a code. Please try again."));
      }
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <DialogHeader>
        <DialogTitle>{COPY[mode].title}</DialogTitle>
        <DialogDescription>{COPY[mode].description}</DialogDescription>
      </DialogHeader>

      <form.Field name="phoneNumber">
        {(field) => (
          <div className="border-input focus-within:ring-ring flex overflow-hidden rounded-md border bg-background focus-within:ring-2 focus-within:ring-offset-2">
            <div className="flex items-center border-r border-input py-2">
              <CountrySelector
                value={countryName}
                onValueChange={(next) => {
                  setCountryName(next);
                  setError("");
                  // Grouping is per numbering plan, so a number typed for the
                  // old country is meaningless under the new one.
                  field.handleChange("");
                }}
              />
            </div>
            <Input
              type="tel"
              aria-label="New phone number"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => {
                setError("");
                field.handleChange(applyPhoneEdit(e.target.value, field.state.value, country));
              }}
              placeholder="Phone number"
              autoComplete="tel"
              // The country selector and this field share one border.
              className="min-w-0 flex-1 rounded-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
        )}
      </form.Field>

      <form.Field name="smsConsent">
        {(field) => (
          <SmsConsentCheckbox
            checked={field.state.value}
            onCheckedChange={(checked) => field.handleChange(checked)}
          />
        )}
      </form.Field>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          data-statsig-name="settings-account-change-phone-cancel"
        >
          Cancel
        </Button>
        <form.Subscribe
          selector={(state) => ({
            ready: toE164(state.values.phoneNumber, country) !== null && state.values.smsConsent,
            submitting: state.isSubmitting,
          })}
        >
          {({ ready, submitting }) => (
            <Button
              type="submit"
              disabled={!ready || submitting}
              data-statsig-name="settings-account-change-phone-send"
            >
              {submitting ? "Sending..." : "Send code"}
            </Button>
          )}
        </form.Subscribe>
      </DialogFooter>
    </form>
  );
};

/** The six-digit code for the number the first step sent to. */
const CodeStep = ({
  sentTo,
  mode,
  onBack,
  onVerified,
}: {
  sentTo: string;
  mode: PhoneDialogMode;
  onBack: () => void;
  onVerified: () => void;
}) => {
  const [error, setError] = useState("");
  const verifyPhoneCode = useVerifyPhoneCode();
  const resendCode = useResendPhoneCode();
  // A verify in flight may still switch the number, so neither leaving the step
  // nor sending another code should race it.
  const busy = verifyPhoneCode.isPending || resendCode.isPending;
  // Closing the dialog unmounts this step but not a verify already sent. Its
  // answer must not close the dialog the user may have opened again since.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleVerify = async (code: string) => {
    try {
      await verifyPhoneCode.mutateAsync({ code });
      // The number changed either way, so the user hears it either way.
      confirmationToast(COPY[mode].verified);
      if (mounted.current) onVerified();
    } catch (err) {
      if (!mounted.current) return;
      // Only a 400 is the code being refused; anything else never judged it.
      const refused = isAxiosError(err) && err.response?.status === 400;
      setError(
        changePhoneFailureMessage(
          err,
          refused
            ? "That code didn't work. Please try again."
            : "We couldn't check your code. Please try again in a moment.",
        ),
      );
    }
  };

  const handleResend = async () => {
    try {
      await resendCode.mutateAsync();
      confirmationToast("Code resent");
      setError("");
    } catch (err) {
      setError(changePhoneFailureMessage(err, "We couldn't resend your code. Please try again."));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>Enter the code</DialogTitle>
        <DialogDescription>
          We texted a 6-digit code to {sentTo}.{COPY[mode].codeNote}
        </DialogDescription>
      </DialogHeader>
      <VerificationCodeInput
        onComplete={handleVerify}
        disabled={verifyPhoneCode.isPending}
        error={error}
        onErrorClear={() => setError("")}
      />
      <DialogFooter>
        <Button
          variant="outline"
          onClick={onBack}
          disabled={busy}
          data-statsig-name="settings-account-change-phone-back"
        >
          Back
        </Button>
        <Button
          variant="outline"
          onClick={handleResend}
          disabled={busy}
          data-statsig-name="settings-account-change-phone-resend"
        >
          {resendCode.isPending ? "Sending..." : "Resend code"}
        </Button>
      </DialogFooter>
    </div>
  );
};
